/* LMC Orlando — Product Detail Page (Chunks 6 + 7).

   Two custom elements:
   - <lmc-pdp-delivery>     wraps the delivery card picker (line item property)
   - <lmc-pdp-sticky-cart>  mobile bottom bar; updates summary on variant changes

   Bundles are real Shopify variants, so <variant-selects> + <product-info>
   from Dawn handle them with no extra JS. */

(function () {
  'use strict';

  /* Delivery choice is intentionally scoped to the CURRENT page view: a
     module-level variable (not storage) so it survives the variant-change
     re-render Dawn does on the surrounding <product-info>, but resets on
     every navigation — every product must default to free Curbside, and a
     White-Glove pick on one chair must never follow the shopper to the
     next. */
  let pageDeliveryChoice = null;

  /* ───────────── Delivery card picker ─────────────
     Each card is a <label> wrapping a hidden radio input. Clicking sets the
     radio + flips the .is-active class. */
  class LMCPDPDelivery extends HTMLElement {
    constructor() {
      super();
      this.onInputChange = this.onInputChange.bind(this);
    }

    connectedCallback() {
      this.cards = this.querySelectorAll('.lmc-opt-card');
      this.inputs = this.querySelectorAll('input[type="radio"]');
      this.restorePersistedChoice();
      this.inputs.forEach((input) => input.addEventListener('change', this.onInputChange));
      // Block change events from bubbling to the ancestor
      // <variant-selects>, which would otherwise treat the delivery
      // pick as a variant option change and trigger a full section
      // re-render that wipes the quantity / state. Delivery is a
      // line-item property, not a Shopify product option.
      this.addEventListener('change', (event) => event.stopPropagation());
    }

    disconnectedCallback() {
      if (this.inputs) {
        this.inputs.forEach((input) => input.removeEventListener('change', this.onInputChange));
      }
    }

    restorePersistedChoice() {
      if (!pageDeliveryChoice) return;
      const match = Array.from(this.inputs).find((input) => input.value === pageDeliveryChoice);
      if (match) this.applyChoice(match);
    }

    onInputChange(event) {
      this.applyChoice(event.currentTarget);
    }

    applyChoice(input) {
      input.checked = true;
      this.cards.forEach((card) => card.classList.remove('is-active'));
      const card = input.closest('.lmc-opt-card');
      if (card) card.classList.add('is-active');
      pageDeliveryChoice = input.value;
      this.dispatchEvent(
        new CustomEvent('lmc:delivery-change', { bubbles: true, detail: { value: input.value } })
      );
    }
  }

  if (!customElements.get('lmc-pdp-delivery')) {
    customElements.define('lmc-pdp-delivery', LMCPDPDelivery);
  }

  /* ───────────── Quantity change isolation ─────────────
     Same problem as the delivery picker above, but for Dawn's stock
     <quantity-input>. When +/− is clicked, the input dispatches a
     'change' that bubbles up. If <quantity-input> sits inside
     <variant-selects> (as it does in our lmc-product-details block
     layout), variant-selects publishes
     PUB_SUB_EVENTS.optionValueSelectionChange and product-info
     fetches a new section, re-rendering the rail and resetting
     quantity back to the Liquid-default value of 1.

     Attach to each <quantity-input> in bubble phase. By the time
     this listener fires, the inner input's own change listener
     (Dawn's QuantityInput.onInputChange) has already run, so qty
     validation still happens. We just stop the bubble before it
     reaches <variant-selects>.

     MutationObserver re-isolates quantity-inputs that appear later
     (e.g., after a variant change re-renders the details rail).
     Dataset flag prevents double-binding. */
  const isolateQuantityChanges = (qty) => {
    if (qty.dataset.lmcQtyIsolated === 'true') return;
    qty.dataset.lmcQtyIsolated = 'true';
    qty.addEventListener('change', (event) => {
      const t = event.target;
      if (t && t.tagName === 'INPUT' && t.getAttribute('name') === 'quantity') {
        event.stopPropagation();
      }
    });
  };
  document.querySelectorAll('quantity-input').forEach(isolateQuantityChanges);
  const qtyObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('quantity-input')) {
          isolateQuantityChanges(node);
        } else if (node.querySelectorAll) {
          node.querySelectorAll('quantity-input').forEach(isolateQuantityChanges);
        }
      }
    }
  });
  qtyObserver.observe(document.body, { childList: true, subtree: true });

  /* ───────────── Sticky mobile cart ─────────────
     Listens for variant changes via Dawn's pub/sub (PUB_SUB_EVENTS.variantChange,
     fired from product-info.js after a variant swap) and our own DOM
     event for delivery changes. Earlier code listened for a DOM
     'variant:change' event that nothing dispatches — the summary
     never refreshed on variant pick. */
  class LMCPDPStickyCart extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.refreshSummary.bind(this);
    }

    connectedCallback() {
      this.summaryEl = this.querySelector('.lmc-pdp-sticky-cart__summary');
      this.titleEl = this.querySelector('.lmc-pdp-sticky-cart__title');
      this.priceBigEl = this.querySelector('[data-sticky-price]');
      this.atcBtn = this.querySelector('[data-sticky-atc]');
      this.onAtcClick = this.onAtcClick.bind(this);
      if (this.atcBtn) this.atcBtn.addEventListener('click', this.onAtcClick);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, this.onChange);
      }
      document.addEventListener('lmc:delivery-change', this.onChange);
      this.refreshSummary();
    }

    disconnectedCallback() {
      if (this.atcBtn) this.atcBtn.removeEventListener('click', this.onAtcClick);
      if (typeof this.variantUnsub === 'function') this.variantUnsub();
      document.removeEventListener('lmc:delivery-change', this.onChange);
    }

    onAtcClick(event) {
      // Forward to the real Add to Cart in the details rail.
      // Strategy:
      //   1. Try the configured #ProductInfo-{id} lookup.
      //   2. Fall back to ANY product-info on the page (there's
      //      only one on a PDP, and Shopify sometimes renames
      //      section ids via the editor so a hardcoded id is
      //      brittle).
      //   3. Last resort: find a submit button with name="add"
      //      anywhere outside the sticky cart itself.
      // requestSubmit(button) is the well-defined "submit this
      // form as if the user clicked this button" API; falls back
      // to button.click() on Safari < 16.
      event.preventDefault();

      const detailsId = this.getDetailsId();
      let realAtc = null;

      const productInfo =
        document.querySelector(`#ProductInfo-${detailsId}`) ||
        document.querySelector('product-info');

      if (productInfo) {
        realAtc = productInfo.querySelector('button[name="add"]');
      }

      if (!realAtc) {
        realAtc = Array.from(document.querySelectorAll('button[name="add"]')).find(
          (btn) => !this.contains(btn)
        );
      }

      if (!realAtc) {
        // eslint-disable-next-line no-console
        console.warn('[lmc-pdp-sticky-cart] real Add to Cart button not found');
        return;
      }
      if (realAtc.disabled) return;

      const form = realAtc.closest('form');
      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit(realAtc);
      } else {
        realAtc.click();
      }
    }

    refreshSummary() {
      // Pull the currently selected variant from <product-info>'s
      // embedded JSON. Used to refresh both the desktop big price
      // and the sticky ATC's enabled/disabled + label so the bar
      // mirrors the real product state after a variant swap.
      //
      // Lookup mirrors onAtcClick's 3-tier resilience: id-based
      // first (fast path), then any product-info on the page
      // (handles Shopify editor renaming section ids).
      let variant = null;
      const productInfo =
        document.querySelector(`#ProductInfo-${this.getDetailsId()}`) ||
        document.querySelector('product-info');
      const selectedJson = productInfo && productInfo.querySelector('[data-selected-variant]');
      if (selectedJson) {
        try {
          variant = JSON.parse(selectedJson.textContent);
        } catch (_) {
          variant = null;
        }
      }

      // Update Affirm monthly estimate(s) — siblings of the sticky
      // cart on the page, addressed via [data-affirm-monthly].
      if (variant && typeof variant.price === 'number') {
        document.querySelectorAll('.lmc-pdp__affirm').forEach((affirm) => {
          const term = parseInt(affirm.dataset.affirmTerm, 10);
          if (!term || term <= 0) return;
          const monthlyEl = affirm.querySelector('[data-affirm-monthly]');
          if (!monthlyEl) return;
          const monthlyCents = Math.round(variant.price / term);
          monthlyEl.textContent = `${this.formatMoneyNoZeros(monthlyCents)}/mo`;
        });
      }

      if (variant) {
        if (this.priceBigEl && typeof variant.price === 'number') {
          this.priceBigEl.textContent = this.formatMoney(variant.price);
        }
        if (this.atcBtn) {
          const isAvailable = variant.available !== false;
          this.atcBtn.disabled = !isAvailable;
          // Localized labels come from data-label-* attributes the
          // Liquid renders via {{ 'products.product.add_to_cart' | t }}.
          const label = isAvailable
            ? this.atcBtn.dataset.labelAvailable
            : this.atcBtn.dataset.labelSoldout;
          if (label) this.atcBtn.textContent = label;
        }
      }

      if (!this.summaryEl) return;
      const parts = [];

      // Selected color swatch
      const swatch = document.querySelector('.lmc-swatch__input:checked');
      if (swatch) {
        const label = swatch.closest('.lmc-swatch')?.querySelector('.lmc-swatch__label')?.textContent?.trim();
        if (label) parts.push(label);
      }

      // Selected bundle
      const bundle = document.querySelector('.lmc-pdp__bundle .lmc-opt-card__input:checked');
      if (bundle) {
        const label = bundle
          .closest('.lmc-opt-card')
          ?.querySelector('.lmc-opt-card__label')
          ?.textContent?.trim();
        if (label) parts.push(label);
      }

      // Selected delivery — only surface text when a delivery picker is
      // actually on the page. Also validate the page-choice fallback
      // against the picker's actual options so a value from a
      // differently-configured picker state can't leak through.
      const deliveryEl = document.querySelector('lmc-pdp-delivery');
      if (deliveryEl) {
        const checkedDelivery = deliveryEl.querySelector('input[type="radio"]:checked');
        let deliveryValue = checkedDelivery?.value;
        if (!deliveryValue && pageDeliveryChoice) {
          const validValues = Array.from(
            deliveryEl.querySelectorAll('input[type="radio"]')
          ).map((input) => input.value);
          if (validValues.includes(pageDeliveryChoice)) deliveryValue = pageDeliveryChoice;
        }
        if (deliveryValue) parts.push(deliveryValue);
      }

      this.summaryEl.textContent = parts.join(' · ');
    }

    getDetailsId() {
      // Section configures which <product-info> we sync to via
      // data-details-id on the host. Matches the section.id of the
      // lmc-product-details section in templates/product.json.
      return this.dataset.detailsId || 'details';
    }

    formatMoneyNoZeros(cents) {
      // Mirror Shopify's `money_without_trailing_zeros` filter:
      // strip the decimal portion when cents % 100 === 0 (no
      // fractional cents). Operate on formatMoney's output so we
      // honor whatever money_format token the merchant configured.
      const formatted = this.formatMoney(cents);
      if (cents % 100 === 0) {
        return formatted.replace(/([.,])00\b/, '');
      }
      return formatted;
    }

    formatMoney(cents) {
      // Prefer Shopify's own formatter when present — it knows about
      // every money_format token the merchant might configure
      // ({{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
      // {{amount_no_decimals_with_comma_separator}}, etc.). Falls back
      // to a token-aware implementation for the standard set when
      // Shopify.formatMoney isn't loaded (e.g., storefronts that
      // didn't ship currency.js).
      const format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, format);
      }
      const value = cents / 100;
      const formatNumber = (decimals, separator) => {
        const fixed = value.toFixed(decimals);
        const [intPart, decPart] = fixed.split('.');
        const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const withSeparator = separator === ',' ? grouped.replace(/,/g, '.') : grouped;
        return decPart ? `${withSeparator}${separator}${decPart}` : withSeparator;
      };
      return format
        .replace(/\{\{\s*amount\s*\}\}/g, formatNumber(2, '.'))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, formatNumber(0, '.'))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatNumber(2, ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, formatNumber(0, ','))
        .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, formatNumber(2, '.').replace(/,/g, ' '))
        .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, formatNumber(0, '.').replace(/,/g, ' '))
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, formatNumber(2, '.').replace(/,/g, "'"));
    }
  }

  if (!customElements.get('lmc-pdp-sticky-cart')) {
    customElements.define('lmc-pdp-sticky-cart', LMCPDPStickyCart);
  }

  /* ───────────── Zoom overlay ─────────────
     Click the magnifying glass on the gallery → fixed-position overlay
     fades in showing the currently active slide's image at viewport
     scale. Esc or click-outside closes. Replaces Dawn's product-modal
     (which expected a data-media-id mapping we didn't have).

     Why a custom element vs inline JS: encapsulates focus restoration
     and scroll-lock without leaking to the rest of the page. */
  class LMCZoom extends HTMLElement {
    constructor() {
      super();
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onBackdropClick = this.onBackdropClick.bind(this);
      this.onCloseClick = this.close.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.imgEl = this.querySelector('[data-lmc-zoom-image]');
      this.closeBtn = this.querySelector('[data-lmc-zoom-close]');
      this.trigger = document.querySelector(`[data-lmc-zoom-trigger="${this.sectionId}"]`);
      if (this.trigger) this.trigger.addEventListener('click', this.onTriggerClick);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.onCloseClick);
      this.addEventListener('click', this.onBackdropClick);
    }

    disconnectedCallback() {
      if (this.trigger) this.trigger.removeEventListener('click', this.onTriggerClick);
      if (this.closeBtn) this.closeBtn.removeEventListener('click', this.onCloseClick);
      document.removeEventListener('keyup', this.onKey);
    }

    onTriggerClick(event) {
      event.preventDefault();
      this.open();
    }

    open() {
      // Magnifying-glass path: open the currently active gallery slide.
      const gallery = document.querySelector(
        `.lmc-pdp-gallery-section[data-section-id="${this.sectionId}"]`
      );
      if (!gallery) return;
      const activeSlide = gallery.querySelector('.lmc-pdp__main.is-active img');
      if (!activeSlide) return;
      // The slide ships a srcset up to 1600w; bump to 2048 for zoom.
      const src = activeSlide.currentSrc || activeSlide.src;
      const upscaled = src ? src.replace(/([?&]width=)\d+/, '$12048') : '';
      this.openWith(upscaled || src, activeSlide.alt || '');
    }

    /* Public: open with a caller-supplied image src + alt. Used by
       <lmc-gallery-lightbox> when a cell is clicked. src should
       already be a full-size URL — the caller decides the width. */
    openWith(src, alt) {
      if (!src || !this.imgEl) return;
      this.imgEl.src = src;
      this.imgEl.srcset = '';
      this.imgEl.alt = alt || '';
      this.lastFocus = document.activeElement;
      this.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keyup', this.onKey);
      requestAnimationFrame(() => {
        this.closeBtn && this.closeBtn.focus();
      });
    }

    close() {
      this.hidden = true;
      // Only release the page scroll lock when no OTHER full-screen
      // modal is still visible — when the zoom was opened on top of
      // the lightbox, closing the zoom shouldn't restore scroll while
      // the lightbox is still up.
      if (!document.querySelector('lmc-gallery-lightbox:not([hidden])')) {
        document.documentElement.style.overflow = '';
      }
      document.removeEventListener('keyup', this.onKey);
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    onKey(event) {
      if (event.key === 'Escape') this.close();
    }

    onBackdropClick(event) {
      // Backdrop = the host element. Clicks on the image or close
      // button bubble through but originate on those children.
      if (event.target === this) this.close();
    }
  }

  if (!customElements.get('lmc-zoom')) {
    customElements.define('lmc-zoom', LMCZoom);
  }

  /* ───────────── Gallery lightbox ─────────────
     Full-screen modal opened by the "+M" overflow tile and the "View
     all N →" link. All media rendered server-side in the section as
     cells; this element handles open/close, tab filtering, and
     pipes cell clicks into <lmc-zoom>.openWith() for the full-size
     view.

     Focus management:
     - On open, focus the close button and remember the trigger as
       lastFocus
     - On close, restore focus to that trigger
     - Esc closes the lightbox (or the zoom if the zoom is open)
  */
  class LMCGalleryLightbox extends HTMLElement {
    constructor() {
      super();
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onCloseClick = this.close.bind(this);
      this.onTabClick = this.onTabClick.bind(this);
      this.onCellClick = this.onCellClick.bind(this);
      this.onKey = this.onKey.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.closeBtn = this.querySelector('[data-lmc-lightbox-close]');
      this.tabs = Array.from(this.querySelectorAll('.lmc-pdp__lightbox-tab'));
      this.cells = Array.from(this.querySelectorAll('.lmc-pdp__lightbox-cell'));
      this.triggers = Array.from(
        document.querySelectorAll(`[data-lmc-lightbox-trigger="${this.sectionId}"]`)
      );

      this.triggers.forEach((t) => t.addEventListener('click', this.onTriggerClick));
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.onCloseClick);
      this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
      this.cells.forEach((cell) => cell.addEventListener('click', this.onCellClick));
    }

    disconnectedCallback() {
      this.triggers.forEach((t) => t.removeEventListener('click', this.onTriggerClick));
      if (this.closeBtn) this.closeBtn.removeEventListener('click', this.onCloseClick);
      this.tabs.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
      this.cells.forEach((cell) => cell.removeEventListener('click', this.onCellClick));
      document.removeEventListener('keyup', this.onKey);
    }

    onTriggerClick(event) {
      event.preventDefault();
      this.lastFocus = event.currentTarget;
      this.open();
    }

    open() {
      this.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keyup', this.onKey);
      requestAnimationFrame(() => {
        if (this.closeBtn) this.closeBtn.focus();
      });
    }

    close() {
      this.hidden = true;
      // Defensive: if a zoom overlay is still up (shouldn't be —
      // Esc would close it first), don't release the page scroll
      // lock yet.
      if (!document.querySelector('lmc-zoom:not([hidden])')) {
        document.documentElement.style.overflow = '';
      }
      document.removeEventListener('keyup', this.onKey);
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    onTabClick(event) {
      const tab = event.currentTarget;
      const filter = tab.dataset.filter;
      if (!filter) return;
      this.tabs.forEach((t) => t.classList.toggle('is-active', t === tab));
      this.cells.forEach((cell) => {
        const tokens = (cell.dataset.filter || '').split(/\s+/);
        cell.hidden = filter !== 'all' && !tokens.includes(filter);
      });
    }

    onCellClick(event) {
      event.preventDefault();
      const cell = event.currentTarget;
      const src = cell.dataset.fullSrc;
      const alt = cell.dataset.alt || '';
      if (!src) return;
      const zoom =
        document.querySelector(`lmc-zoom[data-section-id="${this.sectionId}"]`) ||
        document.querySelector('lmc-zoom');
      if (zoom && typeof zoom.openWith === 'function') {
        zoom.openWith(src, alt);
      }
    }

    onKey(event) {
      if (event.key !== 'Escape') return;
      // If the zoom overlay is open above the lightbox, leave its
      // own Esc handler to close it first; only close the lightbox
      // when the zoom is hidden.
      const zoom = document.querySelector('lmc-zoom:not([hidden])');
      if (zoom) return;
      this.close();
    }
  }

  if (!customElements.get('lmc-gallery-lightbox')) {
    customElements.define('lmc-gallery-lightbox', LMCGalleryLightbox);
  }

  /* Gallery thumb → slide swap is handled by Dawn's media-gallery.js
     (assets/media-gallery.js). It attaches a click handler to each
     thumb button that calls setActiveMedia(target, false), which:
       • removes .is-active from every [data-media-id] slide
       • adds .is-active to the clicked slide
       • sets aria-current="true" on the clicked thumb
     Our CSS (.lmc-pdp__main.is-active { display: block }, others
     display:none) reacts to those class changes. Our previous
     duplicate document-level click handler ran AFTER Dawn's, causing
     a visible flicker (Dawn toggles, then ours re-toggles), so it
     was removed. Leaving this comment so a future contributor doesn't
     re-add it. */

  /* ───────────── Variant → gallery image sync ─────────────
     The PDP renders the gallery and the details rail as two SEPARATE
     sections, so Dawn's product-info.js (details section) can't reach the
     <media-gallery> (gallery section): its `this.querySelector('media-gallery')`
     returns null, so picking a colour never swaps the main image. Bridge it
     here via Dawn's variantChange pub/sub: read the selected variant's
     featured_media and activate the matching gallery slide. Match on the
     media-id SUFFIX because the gallery slides are keyed by the gallery
     section id, which differs from product-info's section id
     (e.g. "…__gallery-123" vs product-info's "…__details"). */
  function syncGalleryToVariant() {
    const productInfo = document.querySelector('product-info');
    const selectedJson = productInfo && productInfo.querySelector('[data-selected-variant]');
    if (!selectedJson) return;
    let variant = null;
    try {
      variant = JSON.parse(selectedJson.textContent);
    } catch (_) {
      return;
    }
    const mediaId = variant && variant.featured_media && variant.featured_media.id;
    if (!mediaId) return;
    const gallery = document.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
    const slide = gallery.querySelector(`[data-media-id$="-${mediaId}"]`);
    if (slide) gallery.setActiveMedia(slide.dataset.mediaId, false);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, syncGalleryToVariant);
  }
})();
